<template>
  <div class="Upsell__Tile" :data-ht-component="`${location}-upsell-tile`">
    <div v-if="selectedVariant" class="Upsell__Upper">
      <a :href="itemUrl" v-if="media('url')" class="Upsell__Media">
        <picture>
          <source media="(min-width:320px)" :srcset="media('url')" />
          <img lazy="eager" :src="media('url')" :alt="media('altText')" />
        </picture>
      </a>
      <div class="Upsell__Summary">
        <div class="Upsell__Details">
          <a :href="itemUrl" class="t4">{{ item.title }}</a>
          <div class="p5" data-color="gray-text" v-if="item.shortDescription" v-html="shortDescription"></div>
        </div>
        <div class="Upsell__Price">
          <Price :item="item" :variant="selectedVariant" location="upsell" :ready="true" />
        </div>
      </div>
    </div>
    <div v-if="selectedVariant" class="Upsell__Lower">
      <div class="Upsell__Dropdowns">
        <Dropdown v-for="(option, index) in options" :selected="data[index].value" @handle-select="onSelect" :key="option.name" :option="option" :location="location" :character-limit="24" />
      </div>

      <div class="Upsell__CTA-Wrapper" :class="multipleOptionsClass">
        <button class="Upsell__CTA t5" :class="outOfStockClass" role="button" type="button" @click="handleClick" :data-ht-component="`${location}-atc`">
          {{ text }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { pubSub } from '@utils/PubSub';
import { arraysEqual, deepClone } from '@utils/performance';
import { removeSoldOutFromText, initCustomAsset } from '@utils/global-functions';
import { ref, computed, onMounted } from 'vue';
import Price from './Price.vue';
import Dropdown from './Dropdown.vue';

const props = defineProps({
  item: {
    type: [Object, Boolean],
    required: true,
    default: false,
  },
  selectionListener: {
    type: Boolean,
    required: false,
    default: false,
  },
  preSelection: {
    type: String,
    required: false,
    default: '',
  },
  location: {
    type: String,
    required: false,
    default: '',
  },
});

const options = ref([]);
const data = ref([]);
const soldout = '(Sold out)';
const selectedVariant = ref(false);

/**
 * @name onSelect
 * @function
 * @param {String} value is the selected value from one of the (possible) several dropdowns in this component
 */
const onSelect = (value) => {
  const selectedValue = removeSoldOutFromText(value);
  const variants = props.item.variants;

  /** @var {Array} clonedOptions creates deep clone of product options */
  const clonedOptions = deepClone(props.item.options);

  /** @var {Boolean} isSingle determines if the product contains a single option. */
  const isSingle = clonedOptions.length <= 1;

  /** @var {Number} index determines the index of the most recently selected dropdown */
  const index = clonedOptions.findIndex((option) => option.values.includes(selectedValue));

  data.value[index] = {
    name: clonedOptions[index].name,
    value: selectedValue,
  };

  const notReady = data.value.includes('');
  if (notReady) return;

  /** @var {Array, Null} unavailableVariants is an initially null that will be overwritten to a filtered array of out-of-stock variants, this will either be an empty array or an array of Objeccts depending on what selections have been made */
  let unavailableVariants;

  if (isSingle) {
    unavailableVariants = variants.filter((match) => match.quantityAvailable <= 0);
  } else {
    /** @var {Array} matches is an initially empty array that will contain possible variants from the selections made */
    const matches = [];
    data.value.forEach((selection) => {
      const matchingVariants = variants.filter((variant) => {
        const { selectedOptions } = variant;

        const match = selectedOptions.find((option) => option.value === selection.value);
        return match;
      });
      matches.push(matchingVariants);
    });

    unavailableVariants = Array.from(new Set(matches.flat())).filter((match) => match.quantityAvailable <= 0);
  }

  clonedOptions.forEach((option, i) => {
    const values = [];
    const { name } = option;

    option.values.forEach((val) => {
      const payload = { name, value: val };
      /** @var {Array} possibleSelection Creates a deep clone of the currently selected options,
       * then updates each option value within the current loops index to determine if an option is unavailable.
       */
      const possibleSelection = deepClone(data.value);
      possibleSelection[i] = payload;

      const match = unavailableVariants.find((variant) => arraysEqual(possibleSelection, variant.selectedOptions)) ?? false;

      if (!match) {
        values.push(val);
      } else {
        values.push(`${val} ${soldout}`);
      }
    });

    option.values = values;
  });

  options.value = clonedOptions;
  fetchVariantFromSelections();
};

const fetchVariantFromSelections = () => {
  const payload = deepClone(data.value);
  const variants = deepClone(props.item.variants);
  const variant = variants.find((variant) => arraysEqual(variant.selectedOptions, payload));
  if (variant) {
    selectedVariant.value = variant;
  } else {
    selectedVariant.value = variants[0];
  }
};

const media = computed(() => {
  return (property = false) => {
    const variant = selectedVariant.value ?? false;
    if (!variant) return;

    if (property) return variant.image[property];

    return;
  };
});

const itemUrl = computed(() => {
  const variant = selectedVariant.value ?? false;
  if (!variant) return props.item.onlineStoreUrl;

  const id = variant.id.split('ProductVariant/')[1].trim();
  return `/products/${props.item.handle}?variant=${id}`;
});

const tags = props?.item?.tags;

const handleClick = () => {
  const promo = window.Brooklinen?.promo?.discounts ?? {};
  let categoryDiscount = 'null',
    discount = 'null',
    tprDiscount = 'null';
  const isLastCall = () => tags.includes('Last Call Assortment');
  const isCategory = () => {
    let categoryTag = tags.some((tag) => tag.includes('Category Promo:'));
    // Adding the not last call check in the case that a category tag accidentally gets added to a last call prod. (Site ops accidentally does this sometimes, so this is a safeguard)
    return categoryTag && !isLastCall();
  };
  if (isCategory()) {
    categoryDiscount = tags
      .find((tag) => tag.includes('Category Promo:'))
      ?.split(':')[1]
      .trim();
  }

  //Compare price is used for last call and TPRs. If the product is not a last call but leverages compare pricing, we can assume it is a TPR
  if (isLastCall()) {
    discount = tags
      .find((tag) => tag.includes('Discount Amount:'))
      ?.split(':')[1]
      .trim();
  } else if (selectedVariant?.value?.compareAtPrice?.amount) {
    tprDiscount = 100 * (1 - parseFloat(selectedVariant?.value?.price?.amount) / parseFloat(selectedVariant?.value?.compareAtPrice?.amount));
  }

  const isGWP = props?.location.includes('gwp') ?? false;
  const variant = selectedVariant.value ?? false;
  if (!variant) return;

  const unavailable = variant.quantityAvailable <= 0;
  if (unavailable) return;

  const msrp = selectedVariant?.value?.compareAtPrice?.amount || selectedVariant?.value?.price?.amount,
    categoryId = isCategory() ? promo?.category?.promoName : 'null';
  const id = variant.id.split('ProductVariant/')[1].trim();
  const quantity = '1';
  const properties = {
    _msrp: msrp,
    _category_id: categoryId,
    _category_discount: categoryDiscount,
    _isGiftCard: selectedVariant?.value?.isGiftCard?.toString(),
    Ships: selectedVariant?.value?.shipsBy?.value,
    _lc_discount: discount,
    _tpr_discount: String(tprDiscount),
  };

  properties['_isGiftCard'] = props.item.handle === 'rise-ai-giftcard';
  if (selectedVariant?.value?.shipsBy) {
    properties['Ships'] = selectedVariant.value.shipsBy?.value;
  }

  if (isGWP) {
    pubSub.publish('theme:gwp:remove');
    properties['_gift-with-purchase'] = true;
    properties['_freeGift'] = true;
  }

  const payload = {
    id,
    quantity,
    properties,
  };
  document.dispatchEvent(
    new CustomEvent('theme:add-to-cart', {
      detail: payload,
    })
  );

  if (isGWP) {
    pubSub.publish('theme:gwp:added');
  }

  const cart = document.querySelector('main-cart');
  if (!cart) return;

  return cart.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest',
  });
};

const text = computed(() => {
  const variant = selectedVariant.value ?? false;
  if (!variant) return 'Add';

  const unavailable = variant.quantityAvailable <= 0;
  return unavailable ? 'Sold out' : 'Add';
});

const shortDescription = computed(() => {
  const description = props?.item?.shortDescription ? props.item.shortDescription.value : false;
  return description ? initCustomAsset(description) : '';
});

const outOfStockClass = computed(() => {
  const variant = selectedVariant.value ?? false;
  if (!variant) return '';

  const unavailable = variant.quantityAvailable <= 0;
  return unavailable ? 'Upsell__CTA--Unavailable' : '';
});

const multipleOptionsClass = computed(() => {
  const hasMultiple = props.item.options.length > 1;
  return hasMultiple ? 'Upsell__CTA-Wrapper--Doubled' : '';
});

const listenForPDPSelections = () => {
  pubSub.subscribe('pdp:selection:update', ({ detail }) => {
    const { value } = detail;
    const allOptions = props.item.options.flatMap((option) => option.values);
    const hasMatchingValue = allOptions.includes(value);
    if (hasMatchingValue) onSelect(value);
  });
};

if (props.selectionListener) listenForPDPSelections();

onMounted(() => {
  let current = 0;
  options.value = props?.item?.options ?? [];
  if (options.value.length <= 0) return;

  do {
    data.value.push('');
    current++;
  } while (current < props.item.options.length);
  props.item.options.forEach((option) => {
    const hasAPreselectedValue = props.preSelection !== '';
    const containsPreselectedValue = hasAPreselectedValue && option.values.includes(props.preSelection);

    if (containsPreselectedValue) {
      onSelect(props.preSelection);
    } else {
      onSelect(option.values[0]);
    }
  });
});
</script>
